import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import { Grid, Box } from 'components/ui';
import { mediaQueries, space } from 'style/config';
import ProductMediaCarousel from 'components/ProductPage/ProductMediaCarousel/ProductMediaCarousel';
import ProductBreadCrumbs from 'components/ProductPage/ProductBreadCrumbs/ProductBreadCrumbs';
import skuUtils from 'utils/Sku';
import Banner from 'components/Content/Banner';
import contentConstants from 'constants/content';
import SkeletonBanner from 'components/Banner/SkeletonBanner/SkeletonBanner';

const { HIDDEN_CATEGORY_ID } = skuUtils;
const { BANNER_TYPES } = contentConstants;

const hasValidBannerContent = bannerData => {
    if (!bannerData || typeof bannerData !== 'object') {
        return false;
    }

    const hasKeys = Object.keys(bannerData).length > 0;

    return hasKeys;
};

const shouldRenderBanner = (bannerData, banner) => (hasValidBannerContent(bannerData) ? banner : <Box />);

function LayoutTop({
    sku = {},
    product,
    content = {},
    showPDPTopOfPageRedesign,
    hideBreadcrumbs,
    preventPageRenderReport,
    displayedMiddleProductBanner,
    displayedProductBanner,
    displayedBottomProductBanner,
    regularProductHasMounted,
    isProductSampleEnabled = false
}) {
    const { parentCategory } = product;
    const isHiddenCategory = parentCategory && parentCategory.categoryId === HIDDEN_CATEGORY_ID;

    // Default to 70px height if not specified on incoming media from CMS.
    const DEFAULT_BANNER_HEIGHT = 70;
    const middleProductBannerHeight = displayedMiddleProductBanner?.media?.height ?? DEFAULT_BANNER_HEIGHT;
    const topProductBannerHeight = displayedProductBanner?.media?.height ?? DEFAULT_BANNER_HEIGHT;

    return (
        <React.Fragment>
            {!isHiddenCategory && !hideBreadcrumbs ? (
                <ProductBreadCrumbs parentCategory={parentCategory} />
            ) : (
                <div
                    css={{
                        height: space[4],
                        [mediaQueries.sm]: {
                            height: space[5]
                        }
                    }}
                />
            )}
            {shouldRenderBanner(
                displayedProductBanner,
                !regularProductHasMounted ? (
                    <SkeletonBanner height={topProductBannerHeight} />
                ) : (
                    <section>
                        <Banner
                            enablePageRenderTracking={false}
                            {...displayedProductBanner}
                            bannerType={BANNER_TYPES.PDP}
                            marginTop={null}
                            marginBottom={[4, 5]}
                        />
                    </section>
                )
            )}
            <Grid
                marginTop={[null, null, 4]}
                marginBottom={[5, 6]}
                lineHeight='tight'
                columns={[null, null, 'minmax(432px, 565px) minmax(480px, 100%)']}
                gap={[3, 4, 2]}
                columnGap={[null, null, 7, 8]}
                gridTemplateRows={[null, null, 'auto 1fr']}
                css={{ position: 'relative' }}
            >
                <div>{content.top}</div>
                <div
                    css={{
                        [mediaQueries.md]: {
                            gridColumn: '1 / 2',
                            gridRow: '1 / 3'
                        }
                    }}
                >
                    <ProductMediaCarousel
                        sku={sku}
                        product={product}
                        showPDPTopOfPageRedesign={showPDPTopOfPageRedesign}
                        preventPageRenderReport={preventPageRenderReport}
                    />
                </div>
                <div>
                    {/* Show skeleton if parent hasn't mounted */}
                    {shouldRenderBanner(
                        displayedMiddleProductBanner,
                        !regularProductHasMounted ? (
                            <SkeletonBanner height={middleProductBannerHeight} />
                        ) : (
                            <section>
                                <Banner
                                    enablePageRenderTracking={false}
                                    {...displayedMiddleProductBanner}
                                    bannerType={isProductSampleEnabled ? BANNER_TYPES.PDP_SAMPLE : BANNER_TYPES.PDP}
                                    marginTop={2}
                                    marginBottom={3}
                                    alignLeft
                                />
                            </section>
                        )
                    )}
                    {content.bottom}
                    {shouldRenderBanner(
                        displayedBottomProductBanner,
                        displayedBottomProductBanner && (
                            <section>
                                <Banner
                                    enablePageRenderTracking={false}
                                    {...displayedBottomProductBanner}
                                    bannerType={BANNER_TYPES.PDP}
                                    marginTop={3}
                                    marginBottom={3}
                                />
                            </section>
                        )
                    )}
                </div>
            </Grid>
        </React.Fragment>
    );
}

export default wrapFunctionalComponent(LayoutTop, 'LayoutTop');
